/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import './DprPipelineSelect.css';

/**
 * Pipeline / mainline picker — same trigger + dropdown pattern as DPR Settings selects.
 */
const DprPipelineSelect = ({
  value,
  options = [],
  onChange,
  placeholder = 'Select pipeline',
  disabled = false,
  className = '',
  appearance = 'pill',
  searchable = false,
  menuFixed = false,
  'aria-label': ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fixedMenuStyle, setFixedMenuStyle] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const useFixedMenu = menuFixed || appearance === 'cell';

  const selected = options.find((o) => String(o.value) === String(value));
  const displayLabel = selected?.label ?? '';

  const filteredOptions = searchable && searchTerm.trim()
    ? options.filter((opt) =>
        String(opt.label || '').toLowerCase().includes(searchTerm.trim().toLowerCase())
      )
    : options;

  const updateFixedMenuPosition = useCallback(() => {
    if (!useFixedMenu || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const optionCount = Math.max(filteredOptions.length, 1);
    const estimatedHeight = Math.min(240, optionCount * 36 + (searchable ? 44 : 8));
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < estimatedHeight + 8 && spaceAbove > spaceBelow;

    setFixedMenuStyle({
      position: 'fixed',
      top: openUp ? Math.max(8, rect.top - estimatedHeight - 4) : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 2500,
    });
  }, [useFixedMenu, filteredOptions.length, searchable]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
      setSearchTerm('');
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!open || !useFixedMenu) return;
    updateFixedMenuPosition();
  }, [open, useFixedMenu, updateFixedMenuPosition, filteredOptions.length, searchTerm]);

  useEffect(() => {
    if (!open || !useFixedMenu) return undefined;
    const onReposition = () => updateFixedMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, useFixedMenu, updateFixedMenuPosition]);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setFixedMenuStyle(null);
    }
  }, [open]);

  const triggerClass =
    appearance === 'field'
      ? 'dpr-pipeline-select-trigger dpr-pipeline-select-trigger--field'
      : appearance === 'cell'
        ? 'dpr-pipeline-select-trigger dpr-pipeline-select-trigger--cell'
        : 'dpr-pipeline-select-trigger';

  const dropdownClassName = useFixedMenu
    ? 'dpr-pipeline-select-dropdown dpr-pipeline-select-dropdown--fixed'
    : 'dpr-pipeline-select-dropdown';

  const handleToggleOpen = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  const dropdown = open ? (
    <div
      ref={dropdownRef}
      className={dropdownClassName}
      role="listbox"
      style={useFixedMenu ? fixedMenuStyle : undefined}
    >
      {searchable ? (
        <div className="dpr-pipeline-select-search">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="dpr-pipeline-select-search__input"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
      {filteredOptions.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="option"
          aria-selected={String(opt.value) === String(value)}
          className="dpr-pipeline-select-opt"
          onClick={() => {
            onChange(opt.value);
            setOpen(false);
          }}
        >
          {opt.label}
        </button>
      ))}
      {filteredOptions.length === 0 ? (
        <div className="dpr-pipeline-select-empty">No matches</div>
      ) : null}
    </div>
  ) : null;

  return (
    <div
      ref={containerRef}
      className={`dpr-pipeline-select-wrap dpr-pipeline-select-wrap--${appearance}${open ? ' dpr-pipeline-select-wrap--open' : ''} ${className}`.trim()}
    >
      <button
        ref={triggerRef}
        type="button"
        className={triggerClass}
        onClick={handleToggleOpen}
        disabled={disabled}
        aria-label={ariaLabel || placeholder}
        aria-expanded={open}
      >
        <span className={`dpr-pipeline-select-value${displayLabel ? '' : ' dpr-pipeline-select-value--placeholder'}`}>
          {displayLabel || placeholder}
        </span>
        {open ? (
          <ChevronUp size={16} color="#6B7280" aria-hidden />
        ) : (
          <ChevronDown size={16} color="#6B7280" aria-hidden />
        )}
      </button>
      {useFixedMenu && fixedMenuStyle && dropdown
        ? createPortal(dropdown, document.body)
        : !useFixedMenu
          ? dropdown
          : null}
    </div>
  );
};

export default DprPipelineSelect;
